import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, ExternalLink, BarChart3, RefreshCw, TrendingUp } from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Products matched to the website purchase buttons. Keys match the backend.
const PRODUCTS = [
  { key: "kochbuch", name: "Kochbuch", button: "Jetzt kaufen" },
  { key: "ernaehrungsplan", name: "Ernährungsplan", button: "Jetzt kaufen" },
  { key: "trainingsplan_2", name: "Trainingsplan 2 Tage", button: "Trainingsplan wählen - 2 Tage" },
  { key: "trainingsplan_3", name: "Trainingsplan 3 Tage", button: "Trainingsplan wählen - 3 Tage" },
  { key: "trainingsplan_4", name: "Trainingsplan 4 Tage", button: "Trainingsplan wählen - 4 Tage" },
  { key: "trainingsplan_5", name: "Trainingsplan 5 Tage", button: "Trainingsplan wählen - 5 Tage" },
  { key: "trainingsplan_6", name: "Trainingsplan 6 Tage", button: "Trainingsplan wählen - 6 Tage" },
];

const NAME_BY_KEY = Object.fromEntries(PRODUCTS.map((p) => [p.key, p.name]));

const AdminPurchaseLinks = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(
    () => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 30000 }),
    [token]
  );

  const [links, setLinks] = useState({});
  const [prices, setPrices] = useState({});
  const [stats, setStats] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  const handleAuthErr = useCallback((e) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 403 && detail === "PASSWORD_CHANGE_REQUIRED") { navigate("/admin/leads", { replace: true }); return true; }
    if ([401, 403].includes(status)) { localStorage.removeItem(TOKEN_KEY); navigate("/admin/login", { replace: true }); return true; }
    return false;
  }, [navigate]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const r = await api.get("/admin/purchase-stats");
      setStats(r.data.stats || []);
      setTotalClicks(r.data.total || 0);
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Statistik laden fehlgeschlagen.");
    } finally { setStatsLoading(false); }
  }, [api, handleAuthErr]);

  const load = useCallback(async () => {
    if (!token) { navigate("/admin/login", { replace: true }); return; }
    setLoading(true);
    try {
      const r = await api.get("/admin/site-content");
      setLinks(r.data.purchaseLinks || {});
      setPrices(r.data.purchasePrices || {});
      await loadStats();
    } catch (e) {
      if (!handleAuthErr(e)) toast.error("Laden fehlgeschlagen.");
    } finally { setLoading(false); }
  }, [api, token, navigate, handleAuthErr, loadStats]);

  useEffect(() => { document.title = "CRM · Kauf-Links"; load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const [rl, rp] = await Promise.all([
        api.patch("/admin/site-content/purchase-links", { links }),
        api.patch("/admin/site-content/purchase-prices", { prices }),
      ]);
      setLinks(rl.data || {});
      setPrices(rp.data || {});
      toast.success("Links & Preise gespeichert.");
    } catch (e) {
      if (!handleAuthErr(e)) toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  const maxCount = Math.max(1, ...stats.map((s) => s.count));

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-purchase-links-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
          <ArrowLeft size={12} /> CRM
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Kauf-Links & Preise</h1>
        <p className="text-[#A8A58F] text-sm mt-1 mb-8">
          Verwalte Stripe-Checkout-Links und Preise pro Produkt. Diese Werte werden direkt von
          den Kaufbuttons der Website verwendet. Leeres Feld setzt auf den Standard zurück.
        </p>

        {loading ? (
          <div className="text-[#C9C6B4] flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Lade…</div>
        ) : (
          <>
            {/* Bestseller / click stats */}
            <div className="glass-strong rounded-2xl p-5 mb-8" data-testid="purchase-stats">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 size={18} className="text-[#B8CFA0]" />
                  <h2 className="font-display text-lg font-black">Bestseller · Klicks auf „Jetzt kaufen"</h2>
                </div>
                <button onClick={loadStats} disabled={statsLoading}
                  className="inline-flex items-center gap-1.5 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                  data-testid="refresh-stats-btn">
                  <RefreshCw size={12} className={statsLoading ? "animate-spin" : ""} /> Aktualisieren
                </button>
              </div>
              <div className="text-[11px] text-[#A8A58F] uppercase tracking-[0.2em] mb-4">
                Gesamt: <span className="text-[#F3F1E8] font-black" data-testid="stats-total">{totalClicks}</span> Klicks
              </div>
              {totalClicks === 0 ? (
                <div className="text-sm text-[#A8A58F]">Noch keine Kauf-Klicks erfasst. Sobald Besucher auf einen Kaufbutton klicken, erscheinen hier die Bestseller.</div>
              ) : (
                <div className="space-y-3">
                  {stats.map((s, idx) => (
                    <div key={s.key} data-testid={`stat-row-${s.key}`}>
                      <div className="flex items-center justify-between gap-3 text-sm mb-1">
                        <span className="flex items-center gap-2 font-semibold text-[#E5E2D0]">
                          {idx === 0 && s.count > 0 && <TrendingUp size={13} className="text-[#B8CFA0]" />}
                          {NAME_BY_KEY[s.key] || s.key}
                        </span>
                        <span className="font-black text-[#F3F1E8]" data-testid={`stat-count-${s.key}`}>{s.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-[#4E5A3B]/60 overflow-hidden">
                        <div className="h-full rounded-full bg-[#B8CFA0] transition-all" style={{ width: `${(s.count / maxCount) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Links & prices editor */}
            <div className="space-y-4">
              {PRODUCTS.map((p) => (
                <div key={p.key} className="glass-strong rounded-2xl p-5" data-testid={`purchase-row-${p.key}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                      <div className="font-display text-lg font-black text-[#F3F1E8]">{p.name}</div>
                      <div className="text-[11px] text-[#A8A58F] mt-0.5">Button: {p.button}</div>
                    </div>
                    {links[p.key] && (
                      <a href={links[p.key]} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#B8CFA0] hover:text-[#D6E8C2] text-xs font-bold" data-testid={`purchase-open-${p.key}`}>
                        <ExternalLink size={12} /> Checkout öffnen
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-[#A8A58F] block mb-1">Preis</label>
                      <input
                        value={prices[p.key] || ""}
                        onChange={(e) => setPrices((v) => ({ ...v, [p.key]: e.target.value }))}
                        placeholder="50 €"
                        className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2.5 text-sm text-[#F3F1E8]"
                        data-testid={`price-input-${p.key}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-[#A8A58F] block mb-1">Stripe-Link</label>
                      <input
                        value={links[p.key] || ""}
                        onChange={(e) => setLinks((l) => ({ ...l, [p.key]: e.target.value }))}
                        placeholder="https://buy.stripe.com/..."
                        className="w-full bg-[#4E5A3B]/60 border border-white/12 rounded-lg px-3 py-2.5 text-sm text-[#F3F1E8] font-mono"
                        data-testid={`purchase-input-${p.key}`}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button onClick={save} disabled={saving}
                className="mt-2 inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] disabled:opacity-50 text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full px-6 py-3 text-sm"
                data-testid="save-purchase-links-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPurchaseLinks;
